import {
	Alert,
	AlertTitle,
	Box,
	Card,
	CardContent,
	Chip,
	CircularProgress,
	Grid,
	LinearProgress,
	Paper,
	Stepper,
	Step,
	StepContent,
	StepLabel,
	Typography,
	alpha,
} from "@mui/material";
import {
	CheckCircle,
	Error,
	PlayArrow,
	Schedule,
	SkipNext,
	Storage,
	Timer,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { formatDateTime } from "../utils/dateUtils";
import StepCheckDetails, { CHECK_STEP_LABELS, checkStepSummary } from "./StepCheckDetails";
import FailureAnalysisPanel from "./FailureAnalysisPanel";

// QUEUED: a run waiting for a free slot (Settings → System limits how many run
// at once, system-wide) or for its turn in a suite that runs tests one at a time
const RUNNING_STATUSES = ["QUEUED", "PENDING", "RUNNING"];

function formatStepDuration(ms) {
	if (ms == null) return null;
	if (ms < 1000) return `${ms}ms`;
	return `${(ms / 1000).toFixed(1)}s`;
}

const deriveExecutionStatus = (execution) => {
	if (!execution) return execution;
	const stepsOk =
		execution?.stepResults?.length > 0 &&
		execution.stepResults.every((s) => {
			const stepStatus = (s.status || "").toUpperCase();
			return (
				stepStatus === "SUCCESS" ||
				stepStatus === "PASSED" ||
				stepStatus === "SKIPPED"
			);
		});
	if (execution?.status === "PENDING" && stepsOk) {
		return { ...execution, status: "SUCCESS" };
	}
	return execution;
};

const normalizeScreenshot = (shot) => {
	if (!shot) return null;
	if (typeof shot === "string") {
		return shot.replace(/[\s\r\n]+/g, "");
	}
	if (shot?.data && Array.isArray(shot.data)) {
		const bytes = new Uint8Array(shot.data);
		let binary = "";
		const chunkSize = 0x8000;
		for (let i = 0; i < bytes.length; i += chunkSize) {
			binary += String.fromCharCode.apply(
				null,
				bytes.subarray(i, i + chunkSize),
			);
		}
		return btoa(binary);
	}
	return null;
};

export default function ExecutionDetailPanel({
	execution,
	liveStatus,
	disableAnimation,
}) {
	if (!execution) return null;

	const normalizedExecution = deriveExecutionStatus(execution);
	const isRunning = RUNNING_STATUSES.includes(normalizedExecution.status);
	const currentStepIndex = liveStatus?.data?.stepIndex ?? -1;

	const steps = normalizedExecution.steps?.length
		? normalizedExecution.steps
		: liveStatus?.data?.results?.map((r) => r.step).filter(Boolean) || [];

	const rawStepResults =
		normalizedExecution.stepResults || normalizedExecution.results || [];

	const stepResults = rawStepResults.map((r) => {
		const normalizedMeta = normalizeScreenshot(r.metadata?.screenshot);
		const normalizedDirect = normalizeScreenshot(r.screenshot);
		return {
			...r,
			screenshot: normalizedDirect,
			metadata: {
				...r.metadata,
				screenshot: normalizedMeta,
			},
		};
	});

	const storedError =
		normalizedExecution.errorMessage ||
		normalizedExecution.error ||
		execution.errorMessage ||
		execution.error;

	const displayedError = liveStatus?.data?.error ?? storedError;

	const chip = (() => {
		switch (normalizedExecution.status) {
			case "SUCCESS":
				return { color: "success", icon: <CheckCircle /> };
			case "FAILED":
				return { color: "error", icon: <Error /> };
			case "RUNNING":
				return { color: "info", icon: <PlayArrow /> };
			case "QUEUED":
			case "PENDING":
				return { color: "warning", icon: <Schedule /> };
			default:
				return { color: "default", icon: <Schedule /> };
		}
	})();

	const content = (
		<Card>
			<CardContent>
				<Box sx={{ mb: 3 }}>
					<Box
						sx={{
							display: "flex",
							justifyContent: "space-between",
							alignItems: "center",
							mb: 2,
						}}
					>
						<Typography variant="h5" fontWeight={600}>
							Execution Details
						</Typography>
						<Chip
							label={normalizedExecution.status}
							color={chip.color}
							icon={chip.icon}
						/>
					</Box>

					{isRunning && (
						<Box sx={{ mb: 2 }}>
							<LinearProgress />
							<Typography
								variant="caption"
								color="text.secondary"
								sx={{ mt: 1 }}
							>
								Executing step {currentStepIndex + 1} of{" "}
								{steps.length || 0}
							</Typography>
						</Box>
					)}

					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								Test Name
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{normalizedExecution.testName || "Unnamed Test"}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography
								variant="caption"
								color="text.secondary"
							>
								Started At
							</Typography>
							<Typography variant="body2">
								{normalizedExecution.startedAt
									? formatDateTime(normalizedExecution.startedAt)
									: ""}
							</Typography>
						</Grid>
						{normalizedExecution.completedAt && (
							<Grid item xs={6}>
								<Typography
									variant="caption"
									color="text.secondary"
								>
									Completed At
								</Typography>
								<Typography variant="body2">
									{formatDateTime(normalizedExecution.completedAt)}
								</Typography>
							</Grid>
						)}
					</Grid>

					{normalizedExecution.dataSetName && (
						<Box
							sx={{
								mt: 2,
								p: 1.5,
								bgcolor: "action.hover",
								borderRadius: 1,
							}}
						>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									gap: 1,
									mb: 1,
								}}
							>
								<Storage fontSize="small" color="primary" />
								<Typography variant="subtitle2">
									Data-Driven:{" "}
									{normalizedExecution.dataSetName}
								</Typography>
								{normalizedExecution.dataRowIndex != null && (
									<Chip
										label={`Row ${normalizedExecution.dataRowIndex + 1}`}
										size="small"
										color="primary"
										variant="outlined"
									/>
								)}
								{normalizedExecution.batchId && (
									<Chip
										label="Batch"
										size="small"
										color="secondary"
										variant="outlined"
									/>
								)}
							</Box>
							{normalizedExecution.dataValues &&
								Object.keys(normalizedExecution.dataValues)
									.length > 0 && (
									<Box
										sx={{
											display: "flex",
											gap: 0.5,
											flexWrap: "wrap",
										}}
									>
										{Object.entries(
											normalizedExecution.dataValues,
										).map(([key, value]) => (
											<Chip
												key={key}
												label={`${key}: ${value}`}
												size="small"
												variant="outlined"
												sx={{
													fontFamily: "monospace",
													fontSize: "0.7rem",
												}}
											/>
										))}
									</Box>
								)}
						</Box>
					)}
				</Box>

				{(normalizedExecution.status === "FAILED" ||
					liveStatus?.event === "failed") &&
					displayedError && (
						<Alert severity="error" sx={{ mb: 3 }}>
							<AlertTitle>Execution Failed</AlertTitle>
							{displayedError}
						</Alert>
					)}

				{normalizedExecution.status === "FAILED" && (
					<FailureAnalysisPanel
						key={normalizedExecution.id}
						executionId={normalizedExecution.id}
						testId={normalizedExecution.testId}
						completedAt={normalizedExecution.completedAt}
					/>
				)}

				<Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
					Test Steps
				</Typography>

				<Stepper orientation="vertical" nonLinear>
					{steps.map((step, index) => {
						const stepResult = stepResults.find((r) => {
							const metaIndex = r.metadata?.stepIndex;
							const idIndex = r.stepId
								? parseInt(r.stepId, 10)
								: null;
							return (
								metaIndex === index ||
								r.stepIndex === index ||
								idIndex === index
							);
						});

						const normalizedStatus = stepResult?.status
							?.toString()
							.toUpperCase();
						const isActive =
							currentStepIndex === index && isRunning;
						const isCompleted =
							normalizedStatus === "SUCCESS" ||
							normalizedStatus === "PASSED";
						const isFailed = normalizedStatus === "FAILED";
						const isSkipped = normalizedStatus === "SKIPPED";
						const currentLiveStatus = liveStatus;
						const liveFailed =
							currentLiveStatus?.event === "step_failed" &&
							currentLiveStatus?.data?.stepIndex === index;

						const shouldExpand =
							isActive || isFailed || liveFailed || isCompleted;

						return (
							<Step
								key={index}
								active={isActive}
								completed={isCompleted}
								expanded={shouldExpand}
							>
								<StepLabel
									error={isFailed || liveFailed}
									icon={
										isFailed || liveFailed ? (
											<Error color="error" />
										) : isSkipped ? (
											<SkipNext color="disabled" />
										) : isCompleted ? (
											<CheckCircle color="success" />
										) : isActive ? (
											<CircularProgress size={20} />
										) : (
											index + 1
										)
									}
								>
									<Box>
										<Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
											<Typography
												variant="body1"
												fontWeight={500}
											>
												{CHECK_STEP_LABELS[step.type] ||
													step.type.charAt(0).toUpperCase() +
														step.type.slice(1)}
											</Typography>
											{isSkipped && (
												<Chip label="Skipped" size="small" />
											)}
											{stepResult?.metadata?.recovery && (
												<Chip
													label="Recovered"
													size="small"
													color="warning"
													variant="outlined"
												/>
											)}
											{stepResult?.duration != null && (
												<Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
													<Timer sx={{ fontSize: 12, color: "text.disabled" }} />
													<Typography
														variant="caption"
														color="text.disabled"
													>
														{formatStepDuration(stepResult.duration)}
													</Typography>
												</Box>
											)}
										</Box>
										{step.selector && (
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{
													fontFamily: "monospace",
												}}
											>
												{step.selector}
											</Typography>
										)}
										{stepResult?.metadata?.recovery && (
											<Typography
												variant="caption"
												color="warning.main"
												display="block"
											>
												Recovered: {stepResult.metadata.recovery}
											</Typography>
										)}
										{(step.url || step.value) && (
											<Typography
												variant="caption"
												color="text.secondary"
												display="block"
											>
												{step.url || step.value}
											</Typography>
										)}
										{CHECK_STEP_LABELS[step.type] && (
											<Typography variant="caption" color="text.secondary" display="block">
												{checkStepSummary(step)}
											</Typography>
										)}
									</Box>
								</StepLabel>
								<StepContent>
									{stepResult && (
										<Paper
											elevation={0}
											sx={{
												p: 2,
												bgcolor: (theme) =>
													alpha(
														theme.palette[isFailed ? "error" : "success"].main,
														theme.palette.mode === "dark" ? 0.14 : 0.08,
													),
												borderLeft: 3,
												borderColor: isFailed
													? "error.main"
													: "success.main",
												color: "text.primary",
											}}
										>
											{isFailed &&
												(stepResult.errorMessage ||
													stepResult.error) && (
													<>
														<Typography
															variant="caption"
															fontWeight={600}
														>
															Error:
														</Typography>
														<Typography variant="body2">
															{stepResult.errorMessage ||
																stepResult.error}
														</Typography>
													</>
												)}

											{isCompleted && (
												<Typography variant="body2">
													Step completed successfully
												</Typography>
											)}
											<StepCheckDetails
												stepResult={stepResult}
												executionId={normalizedExecution.id}
												stepIndex={index}
											/>
										</Paper>
									)}

									{(isFailed || liveFailed) &&
										(() => {
											const rawScreenshot =
												stepResult?.metadata
													?.screenshot ||
												stepResult?.screenshot ||
												(currentLiveStatus?.event ===
													"step_failed" &&
													currentLiveStatus?.data
														?.stepIndex === index &&
													currentLiveStatus?.data
														?.screenshot);

											const screenshot =
												normalizeScreenshot(
													rawScreenshot,
												);

											return screenshot ? (
												<Box sx={{ mt: 2 }}>
													<Typography
														variant="caption"
														color="error"
														fontWeight={600}
														display="block"
														sx={{ mb: 1 }}
													>
														Failure Screenshot
													</Typography>
													<Paper
														elevation={2}
														sx={{
															p: 1,
															bgcolor: "action.hover",
															maxHeight: 400,
															overflow: "auto",
															display: "flex",
															justifyContent:
																"center",
															alignItems:
																"center",
															minHeight: 200,
															border: "2px solid",
															borderColor:
																"error.main",
														}}
													>
														<img
															src={`data:image/jpeg;base64,${screenshot}`}
															alt={`Step ${index + 1} failure screenshot`}
															style={{
																width: "100%",
																height: "auto",
																display:
																	"block",
															}}
														/>
													</Paper>
												</Box>
											) : null;
										})()}

									{currentLiveStatus?.event ===
										"step_failed" &&
										currentLiveStatus?.data?.stepIndex ===
											index && (
											<Paper
												elevation={0}
												sx={{
													p: 2,
													bgcolor: (theme) =>
														alpha(
															theme.palette.error.main,
															theme.palette.mode === "dark" ? 0.14 : 0.08,
														),
													borderLeft: 3,
													borderColor: "error.main",
													color: "text.primary",
													mt: 1,
												}}
											>
												<Typography
													variant="caption"
													fontWeight={600}
												>
													Error:
												</Typography>
												<Typography variant="body2">
													{
														currentLiveStatus.data
															.error
													}
												</Typography>
											</Paper>
										)}
								</StepContent>
							</Step>
						);
					})}
				</Stepper>
			</CardContent>
		</Card>
	);

	if (disableAnimation) return content;

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
		>
			{content}
		</motion.div>
	);
}
